/**
 * Pack selection + availability sync for the Searchanise search results page.
 *
 * INITIAL PACK SELECTION (replaces the retired snize-wholesale-pack-sync.js):
 * once per card, pick which pack to highlight by audience —
 * - guests / retail customers: the SMALLEST pack count with stock; if the
 *   smallest is sold out, the next smallest in stock; all sold out → the
 *   smallest (the availability logic below then shows the OOS presentation).
 * - wholesale customers (customer tag "WHS", read from the global
 *   SearchaniseCustomerTags array theme.liquid prints on every page): the
 *   LARGEST pack count with stock, degrading downwards the same way.
 * Selection clicks the preferred pack button so Searchanise re-renders and
 * the WPD wholesale app reprices that variant. For wholesale, an
 * already-highlighted pack still gets a synthetic click when it is not the
 * product's FIRST variant: WPD's initial rewrite prices the first variant
 * only, and only its click path reprices the selected one — skipping the
 * click would show the wrong wholesale price. A user's own pack click marks
 * the card as chosen, so late-arriving data never overrides it.
 *
 * On /pages/search-results-page each product card gets its buttons from two
 * competing sources: the Searchanise widget rebuilds .snize-buttons-bottom
 * from the SELECTED pack's availability on every ".snize-size" click, while
 * the dashboard CustomJS appends its own "Add to cart" button based on the
 * PRODUCT-level quantity on Searchanise.ResultsUpdated. When a sold-out pack
 * is selected the two overlap: the card shows the widget's "View product"
 * AND the CustomJS "Add to cart" at once, and none of the out-of-stock
 * presentation (grey button, "Currently Unavailable" / "Coming Soon!"
 * labels) kicks in because those hang off the card's
 * "snize-product-out-of-stock" class, which reflects product-level stock.
 *
 * This script keeps each card consistent with its selected pack, with all
 * work designed to leave no visible intermediate frames:
 *
 * - A CAPTURE-phase click listener toggles "snize-product-out-of-stock" on
 *   the card synchronously, BEFORE the widget's own delegated handler
 *   re-renders the buttons — so a sold-out pack's "View product" button is
 *   born grey and the label is already showing, all within one frame.
 *   Synthetic clicks from snize-wholesale-pack-sync.js take the same path.
 * - An injected <style> hides every add-to-cart control on cards carrying
 *   the out-of-stock class, so whatever the dashboard CustomJS appends
 *   later is hidden the instant it enters the DOM — no JS timing window.
 * - A debounced MutationObserver scan reconciles everything else (initial
 *   render, pagination, keyboard pack selection). It only acts on POSITIVE
 *   evidence: a card is only restored when the selected pack is confirmed
 *   available, never because data was momentarily unreadable — transient
 *   states must not strip the out-of-stock presentation. Mutations outside
 *   the #snize_results container (e.g. the header shipping countdown that
 *   ticks every second) are ignored.
 *
 * Depends on the Searchanise widget's card markup (.snize-product,
 * .snize-size data-value/data-variant, .snize-buttons-bottom, jQuery
 * data('snize_item_data').shopify_variants[].available) and on the dashboard
 * CustomCSS keying the out-of-stock labels off "snize-product-out-of-stock";
 * if the dashboard template is reworked, revisit this file.
 *
 * jQuery gotcha: the widget stores snize_item_data (and triggers its
 * "Searchanise.ResultsUpdated" event) through its PRIVATE jQuery instance,
 * window.Searchanise.$ (created via jQuery.noConflict(true)). jQuery .data()
 * and custom events are isolated per instance, so reading through the
 * theme's window.jQuery silently returns undefined — always go through
 * Searchanise.$ first.
 *
 * Loaded from layout/theme.liquid on the search-results-page for all
 * visitors (unlike snize-wholesale-pack-sync.js, which is wholesale-only).
 */
(function () {
  var APPLIED_ATTR = 'data-pack-oos-applied';   // card class was added by us
  var INJECTED_ATTR = 'data-pack-oos-injected'; // fallback button added by us
  var PREF_ATTR = 'data-pack-pref-synced';      // initial pack already chosen
  var STYLE_ID = 'snize-pack-availability-style';

  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent =
      'li.snize-product.snize-product-out-of-stock .snize-add-to-cart-button,' +
      'li.snize-product.snize-product-out-of-stock .snize-initial-add-button,' +
      'li.snize-product.snize-product-out-of-stock .snize-qty-add-wrapper' +
      '{ display: none !important; }';
    document.head.appendChild(style);
  }

  // The widget's private jQuery instance is the only one that can read the
  // data it stored; window.jQuery is a last-resort fallback that will only
  // work if the store ever flips Searchanise.forceUseExternalJQuery on.
  function snizeJQ() {
    if (window.Searchanise && window.Searchanise.$) return window.Searchanise.$;
    return window.jQuery || null;
  }

  function viewProductLabel() {
    try {
      var widgets = window.Searchanise && window.Searchanise.GetWidgets && window.Searchanise.GetWidgets();
      if (widgets && widgets.options && widgets.options.LabelResultsViewProduct) {
        return widgets.options.LabelResultsViewProduct;
      }
    } catch (e) {
      /* fall through to default */
    }
    return 'View product';
  }

  function itemVariants(card) {
    var jq = snizeJQ();
    var data = jq ? jq(card).data('snize_item_data') : null;
    var variants = data && data.shopify_variants;
    return variants && variants.length ? variants : null;
  }

  // The variant a pack button stands for, or null when it cannot be
  // determined (no item data, unknown option value).
  function variantForButton(card, button) {
    var variants = itemVariants(card);
    if (!variants) return null;

    var i, v;

    // Exact match on the variant id the dashboard CustomJS stamps on the
    // pack buttons, when present.
    var variantId = button.getAttribute('data-variant');
    if (variantId) {
      for (i = 0; i < variants.length; i++) {
        v = variants[i];
        if (v && String(v.variant_id) === String(variantId)) return v;
      }
    }

    // Fallback: match the button's option value against variant options.
    var value = button.getAttribute('data-value');
    if (value == null) return null;
    for (i = 0; i < variants.length; i++) {
      v = variants[i];
      if (!v || !v.options) continue;
      if (v.option_size) {
        if (String(v.options[v.option_size]) === value) return v;
      } else {
        for (var key in v.options) {
          if (Object.prototype.hasOwnProperty.call(v.options, key) && String(v.options[key]) === value) {
            return v;
          }
        }
      }
    }
    return null;
  }

  function selectedVariant(card) {
    var active = card.querySelector('.snize-size-active');
    return active ? variantForButton(card, active) : null;
  }

  function applyOutOfStock(card) {
    // Cards Searchanise itself rendered as out of stock already look right;
    // only claim the class (for later restore) when we are the ones adding it.
    if (!card.classList.contains('snize-product-out-of-stock')) {
      card.setAttribute(APPLIED_ATTR, card.classList.contains('snize-product-in-stock') ? 'in-stock' : '1');
      card.classList.add('snize-product-out-of-stock');
      card.classList.remove('snize-product-in-stock');
    }

    // The widget normally renders "View product" itself when a sold-out pack
    // is clicked; inject one only if the card would otherwise have no button.
    var bottom = card.querySelector('.snize-buttons-bottom');
    if (bottom && !bottom.querySelector('.snize-view-product-button')) {
      var button = document.createElement('button');
      button.className = 'snize-button snize-action-button snize-button-shape-square snize-view-product-button';
      button.setAttribute(INJECTED_ATTR, '1');
      button.textContent = viewProductLabel();
      bottom.appendChild(button);
    }
  }

  function restore(card) {
    if (card.hasAttribute(APPLIED_ATTR)) {
      if (card.getAttribute(APPLIED_ATTR) === 'in-stock') {
        card.classList.add('snize-product-in-stock');
      }
      card.classList.remove('snize-product-out-of-stock');
      card.removeAttribute(APPLIED_ATTR);
    }

    var injected = card.querySelectorAll('[' + INJECTED_ATTR + ']');
    for (var i = 0; i < injected.length; i++) {
      injected[i].parentNode.removeChild(injected[i]);
    }
  }

  // Only act on positive evidence. When the variant cannot be determined
  // (data momentarily unreadable, buttons mid-rebuild) leave the card
  // exactly as it is — restoring on doubt is what causes visible flicker.
  function syncTo(card, variant) {
    if (!variant || variant.available == null) return;
    if (Number(variant.available) > 0) {
      restore(card);
    } else {
      applyOutOfStock(card);
    }
  }

  function syncCard(card) {
    syncTo(card, selectedVariant(card));
  }

  function isWholesale() {
    var tags = window.SearchaniseCustomerTags;
    if (!tags || !tags.length) return false;
    for (var i = 0; i < tags.length; i++) {
      if (String(tags[i]).toUpperCase() === 'WHS') return true;
    }
    return false;
  }

  // "100 Pack" → 100; null when the option value carries no number.
  function packCount(button) {
    var match = /\d+/.exec(button.getAttribute('data-value') || '');
    return match ? parseInt(match[0], 10) : null;
  }

  // Once per card: highlight the audience-preferred pack (see header).
  // Runs only when every pack resolved to a variant — otherwise the card is
  // left unmarked and the next scan retries with (hopefully) complete data.
  function selectPreferredPack(card) {
    if (card.hasAttribute(PREF_ATTR)) return;
    var buttons = card.querySelectorAll('.snize-size');
    if (!buttons.length) return;

    var variants = itemVariants(card);
    if (!variants) return;

    var entries = [];
    for (var i = 0; i < buttons.length; i++) {
      var count = packCount(buttons[i]);
      if (count == null) {
        // Non-numeric pack option: no defined order, keep Searchanise's own
        // default highlight for this card.
        card.setAttribute(PREF_ATTR, '1');
        return;
      }
      var variant = variantForButton(card, buttons[i]);
      if (!variant || variant.available == null) return;
      entries.push({ button: buttons[i], count: count, variant: variant });
    }

    var wholesale = isWholesale();
    entries.sort(function (a, b) {
      return wholesale ? b.count - a.count : a.count - b.count;
    });

    var preferred = null;
    for (i = 0; i < entries.length; i++) {
      if (Number(entries[i].variant.available) > 0) {
        preferred = entries[i];
        break;
      }
    }
    if (!preferred) preferred = entries[0]; // everything sold out

    card.setAttribute(PREF_ATTR, '1');

    var isActive = preferred.button.classList.contains('snize-size-active');
    var isFirstVariant = variants[0] && String(variants[0].variant_id) === String(preferred.variant.variant_id);
    // Retail: click only when the highlight has to move. Wholesale: also
    // re-click an already-active non-first variant so the WPD app reprices
    // it via its click path (its initial rewrite priced the first variant).
    if (!isActive || (wholesale && !isFirstVariant)) {
      preferred.button.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
    } else {
      syncTo(card, preferred.variant);
    }
  }

  var scanScheduled = false;
  function scheduleScan() {
    if (scanScheduled) return;
    scanScheduled = true;
    setTimeout(function () {
      scanScheduled = false;
      var cards = document.querySelectorAll('.snize-search-results .snize-product');
      for (var i = 0; i < cards.length; i++) {
        selectPreferredPack(cards[i]);
        syncCard(cards[i]);
      }
    }, 100);
  }

  // Runs in the capture phase, before the widget's delegated click handler
  // re-renders the card's buttons: the clicked pack is about to become the
  // active one, so the out-of-stock class is already in place (or removed)
  // when the widget paints the new button.
  function onCaptureClick(event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var button = target.closest('.snize-size');
    if (!button) return;
    var card = button.closest('.snize-product');
    if (!card) return;
    // Any pack click — the user's or our synthetic one — counts as the
    // card's choice; late-arriving data must not override it.
    card.setAttribute(PREF_ATTR, '1');
    syncTo(card, variantForButton(card, button));
    scheduleScan();
  }

  // The widget triggers Searchanise.ResultsUpdated through its private
  // jQuery instance, so the listener must be bound through that same
  // instance. It only exists once init.js finishes loading, which it
  // announces with a NATIVE "Searchanise.Loaded" document event.
  var eventBound = false;
  function bindResultsUpdated() {
    if (eventBound) return;
    if (window.Searchanise && window.Searchanise.$) {
      eventBound = true;
      window.Searchanise.$(document).on('Searchanise.ResultsUpdated', scheduleScan);
    }
  }

  // Ignore mutations outside the Searchanise results container (the header
  // shipping countdown mutates every second); before the container exists,
  // let everything through so the initial render is never missed.
  function touchesResults(records) {
    var results = document.getElementById('snize_results');
    if (!results) return true;
    for (var i = 0; i < records.length; i++) {
      var target = records[i].target;
      if (target && results.contains(target)) return true;
    }
    return false;
  }

  function start() {
    injectStyle();
    new MutationObserver(function (records) {
      if (touchesResults(records)) scheduleScan();
    }).observe(document.body, {
      childList: true,
      subtree: true
    });
    document.addEventListener('click', onCaptureClick, true);
    bindResultsUpdated();
    document.addEventListener('Searchanise.Loaded', function () {
      bindResultsUpdated();
      scheduleScan();
    });
    scheduleScan();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
